// ProductPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./productPage.css";
import { useProduct } from "../../context/ProductContext";
import { toast } from "react-toastify";
import Products from "../product/Products";
import WhyChooseUs from "../WhyChooseUs/WhyChooseUs";

const ProductPage = () => {
  const { productToShow, cart, setCart } = useProduct();
  const [faqOpen, setFaqOpen] = useState({ a: true, b: false, c: false });
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const inCart = useMemo(
    () => (productToShow ? cart.some((i) => i.id === productToShow.id) : false),
    [cart, productToShow]
  );

  const handleCart = (item, q = 1) => {
    if (!item) return;
    if (!cart.some((i) => i.id === item.id)) {
      const withQty = { ...item, qty: q };
      setCart((prev) => [...prev, withQty]);
      toast.success(`${item.name} added to cart`);
    } else {
      toast.warning(`${item.name} is already in your cart`);
    }
  };

  const handleBuyNow = () => {
    handleCart(productToShow, qty);
    toast.info("Item added — you can complete your order from the cart.");
  };

  if (!productToShow) {
    return (
      <div className="ppx-loading-wrap">
        <div className="ppx-skel-hero" />
        <div className="ppx-skel-grid">
          <div className="ppx-skel-card" />
          <div className="ppx-skel-card" />
        </div>
      </div>
    );
  }

  const {
    name,
    img,
    desc,
    price,
    license,
    compatibility,
    highlights,
    idealFor,
    support,
    mrp,
  } = productToShow;

  const hasDiscount = mrp && Number(mrp) > Number(price);
  const saveAmt = hasDiscount ? Number(mrp) - Number(price) : 0;
  const savePct = hasDiscount ? Math.round((saveAmt / Number(mrp)) * 100) : 0;

  const safeLicense = license || "1–3 Devices / 1 Year";
  const safeCompatibility = compatibility?.length
    ? compatibility.join(", ")
    : "Windows, macOS, Android";
  const safeIdealFor = idealFor || "Home, family & small teams";
  const safeSupport = support || "Email + remote assistance";

  return (
    <>
      {/* HERO */}
      <header className="ppx-hero ppx-hero--new">
        <div className="ppx-hero-inner ppx-hero-grid">
          {/* LEFT SIDE */}
          <div className="ppx-hero-left">
            <div className="ppx-breadcrumbs" aria-label="breadcrumbs">
              <span>Home</span> <span>›</span> <span>All Products</span>{" "}
              <span>›</span> <strong>{name}</strong>
            </div>

            <div className="ppx-pill-row">
              <span className="ppx-pill">Genuine Digital License</span>
              <span className="ppx-pill ppx-pill--soft">
                Instant Email Delivery
              </span>
            </div>

            <h1 className="ppx-title">{name}</h1>

            <p className="ppx-sub">
              Get a verified digital license with guided setup so your everyday
              browsing, work, and online payments can stay safer on{" "}
              {safeCompatibility}.
            </p>

            <ul className="ppx-hero-bullets" role="list">
              <li>⚡ License key &amp; instructions emailed within minutes</li>
              <li>🛡 Helps block viruses, malware, and risky websites</li>
              <li>🧩 Supports {safeCompatibility}</li>
              <li>🤝 Human support available if you need activation help</li>
            </ul>
          </div>

          {/* RIGHT SIDE – PURCHASE PANEL */}
          <aside className="ppx-hero-card" aria-label="purchase panel">
            <div className="ppx-hero-card-imgwrap">
              <img src={img} alt={name} className="ppx-img" />
              <div className="ppx-tag">Instant Digital License</div>
              {hasDiscount && (
                <div className="ppx-tag ppx-tag--save">
                  Save {savePct}% today
                </div>
              )}
            </div>

            <div className="ppx-price-row">
              <div>
                {hasDiscount && (
                  <div className="ppx-mrp">
                    MRP <s>${Number(mrp).toFixed(0)}</s>{" "}
                    <span className="ppx-save">
                      You save ${saveAmt.toFixed(0)}
                    </span>
                  </div>
                )}
                <div className="ppx-price">
                  <span className="ppx-currency">$</span>
                  {Number(price).toFixed(0)}.00
                </div>
                <div className="ppx-emi">
                  Includes: <strong>{safeLicense}</strong>
                  <span className="ppx-emi-dot">•</span>
                  Digital delivery
                  <span className="ppx-emi-dot">•</span>
                  Tax invoice
                </div>
              </div>

              <div className="ppx-qty">
                <label htmlFor="qty">Seats / Licenses</label>
                <select
                  id="qty"
                  value={qty}
                  onChange={(e) => setQty(Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ppx-actions">
              <button
                onClick={() => handleCart(productToShow, qty)}
                className={`ppx-btn ${inCart ? "is-in" : ""}`}
              >
                {inCart ? "✔ Added to Cart" : "Add to Cart"}
              </button>
              <button onClick={handleBuyNow} className="ppx-btn ppx-btn--solid">
                Buy Now
              </button>
            </div>

            <div className="ppx-includes">
              <span className="ppx-chip">✔ Official Product Key</span>
              <span className="ppx-chip">✔ Step-by-step Setup Guide</span>
              <span className="ppx-chip">✔ Automatic Updates (as per plan)</span>
              <span className="ppx-chip">✔ Help with install &amp; renewal</span>
            </div>
          </aside>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <section className="ppx-section">
        <div className="ppx-wrap ppx-wrap--new">
          {/* MAIN INFO */}
          <main className="ppx-right">
            <div className="ppx-meta">
              <div className="ppx-stars" aria-label="rating 4.8 out of 5">
                <span>★</span>
                <span>★</span>
                <span>★</span>
                <span>★</span>
                <span className="ppx-star-dim">★</span>
                <em>Popular choice with recent buyers</em>
              </div>
              <p className="ppx-desc">
                {desc ||
                  "This plan is designed to help protect your devices in real time while staying light on system resources. It’s well-suited for everyday browsing, streaming, and online payments, with a straightforward activation process and renewal reminders."}
              </p>
            </div>

            {/* HIGHLIGHTS */}
            <section className="ppx-features ppx-features--new">
              <h2 className="ppx-h2">What you get with this plan</h2>
              <ul className="ppx-grid">
                {(highlights && highlights.length
                  ? highlights
                  : [
                      "Threat monitoring for files, downloads, and apps",
                      "Web protection aimed at blocking risky links and sites",
                      "Safer environment for banking, shopping, and payments",
                      "Automatic updates running quietly in the background",
                      "Low impact on performance for work and gaming",
                      "Guided help if you need support during setup",
                    ]
                ).map((feature, i) => (
                  <li key={i} className="ppx-feat">
                    <span className="ppx-tick" aria-hidden>
                      ✓
                    </span>
                    <p>{feature}</p>
                  </li>
                ))}
              </ul>
            </section>

            {/* SPECS – CARDS */}
            <section className="ppx-spec-cards">
              <div className="ppx-spec-card">
                <h3>License Type</h3>
                <p>{safeLicense}</p>
              </div>
              <div className="ppx-spec-card">
                <h3>Supported Platforms</h3>
                <p>{safeCompatibility}</p>
              </div>
              <div className="ppx-spec-card">
                <h3>Best Suited For</h3>
                <p>{safeIdealFor}</p>
              </div>
              <div className="ppx-spec-card">
                <h3>Support Coverage</h3>
                <p>{safeSupport}</p>
              </div>
            </section>

            {/* FAQ */}
            <section className="ppx-acc ppx-acc--faq">
              <button
                className="ppx-acc-head"
                onClick={() => setFaqOpen((s) => ({ ...s, a: !s.a }))}
                aria-expanded={faqOpen.a}
              >
                <span>How fast will I receive my license key?</span>
                <span className={`ppx-caret ${faqOpen.a ? "open" : ""}`}>▾</span>
              </button>
              {faqOpen.a && (
                <div className="ppx-acc-body">
                  <p>
                    Delivery is digital. After your payment is confirmed, your
                    license key and activation steps are sent to your registered
                    email address. In most cases this happens within a few
                    minutes.
                  </p>
                </div>
              )}

              <button
                className="ppx-acc-head"
                onClick={() => setFaqOpen((s) => ({ ...s, b: !s.b }))}
                aria-expanded={faqOpen.b}
              >
                <span>Can I reinstall or move it to another device?</span>
                <span className={`ppx-caret ${faqOpen.b ? "open" : ""}`}>▾</span>
              </button>
              {faqOpen.b && (
                <div className="ppx-acc-body">
                  <p>
                    Most plans allow you to reinstall on the same device or move
                    to a new one, as long as you stay within the total device
                    limit in your license. If you&apos;re unsure, share your
                    order details with us and we&apos;ll confirm the options.
                  </p>
                </div>
              )}

              <button
                className="ppx-acc-head"
                onClick={() => setFaqOpen((s) => ({ ...s, c: !s.c }))}
                aria-expanded={faqOpen.c}
              >
                <span>What if something goes wrong during activation?</span>
                <span className={`ppx-caret ${faqOpen.c ? "open" : ""}`}>▾</span>
              </button>
              {faqOpen.c && (
                <div className="ppx-acc-body">
                  <p>
                    If you run into any issues, our team can help with
                    step-by-step guidance over chat, email, or remote support,
                    until the product is fully installed and activated on your
                    device.
                  </p>
                </div>
              )}
            </section>

            <div className="ppx-help">
              Still deciding?{" "}
              <span className="ppx-link">
                Talk to us and we&apos;ll help you pick the right plan.
              </span>
            </div>
          </main>

          {/* RIGHT SIDE – QUICK SPECS */}
          <aside className="ppx-left ppx-left--new">
            <ul className="ppx-spec">
              <li>
                <span>License</span>
                <strong>{safeLicense}</strong>
              </li>
              <li>
                <span>Platforms</span>
                <strong>{safeCompatibility}</strong>
              </li>
              <li>
                <span>Ideal For</span>
                <strong>{safeIdealFor}</strong>
              </li>
              <li>
                <span>Support</span>
                <strong>{safeSupport}</strong>
              </li>
            </ul>
          </aside>
        </div>
      </section>

      {/* MORE PRODUCTS / TRUST SECTION */}
      <Products />

      {/* MOBILE STICKY BAR */}
      <div className="ppx-sticky-buy">
        <div className="ppx-sticky-price-wrap">
          <div className="ppx-sticky-label">Today&apos;s price</div>
          <div className="ppx-sticky-price">
            <span className="ppx-currency">$</span>
            {Number(price).toFixed(0)}
          </div>
        </div>
        <button
          onClick={() => handleCart(productToShow, qty)}
          className={`ppx-sticky-btn ${inCart ? "is-in" : ""}`}
        >
          {inCart ? "✔ In Cart" : "Add to Cart"}
        </button>
        <button
          onClick={handleBuyNow}
          className="ppx-sticky-btn ppx-sticky-buy-now"
        >
          Buy Now
        </button>
      </div>
    </>
  );
};

export default ProductPage;
